import { useCallback, useContext } from 'react';
import { handleError } from '@src/utils/error';
import { useDispatch } from 'react-redux';
import useAxios from '@src/hooks/useAxios';
import { layoutItems } from '@src/routes/stencils';
import {
  replaceLayoutImageItems,
  saveSnapshot,
  setLayoutTemplateName
} from '../../actions/wysiwyg/editor';
import { PageContentStateContext } from '@src/contexts/wysiwyg/page_content';
import { isTextBox } from '@src/utils/wysiwyg_editor/data';
import { TextCacheDispatchContext } from '@src/contexts/wysiwyg/text_cache';
import type { TPageContentItem } from '@src/types/wysiwyg_editor';
import useAutoPopulateImagesInLayout from './useAutoPopulateImagesInLayout';

function handleGetLayoutError(error: Error, layoutName: string) {
  const alertMessage =
    'There was a problem selecting that layout. Please try again.';

  alert(alertMessage);

  handleError(error, `Error loading stencil layout: ${layoutName}`);
}

function useChangeLayout(): (newLayoutName: string) => void {
  const axios = useAxios();
  const dispatch = useDispatch();
  const { contentItems } = useContext(PageContentStateContext);
  const { add: addToCache } = useContext(TextCacheDispatchContext);
  const autoPopulateImagesInLayout = useAutoPopulateImagesInLayout();

  const changeLayout = useCallback(
    (newLayoutName: string) => {
      axios
        .get<Array<TPageContentItem>>(layoutItems, {
          params: { id: newLayoutName }
        })
        .then((res) => {
          dispatch(saveSnapshot());
          dispatch(setLayoutTemplateName(newLayoutName));
          dispatch(replaceLayoutImageItems(res.data));

          autoPopulateImagesInLayout(contentItems, res.data);

          // add old text items to the cache
          contentItems.filter(isTextBox).forEach(addToCache);
        })
        .catch((error) => handleGetLayoutError(error, newLayoutName));
    },
    [addToCache, autoPopulateImagesInLayout, axios, contentItems, dispatch]
  );

  return changeLayout;
}

export default useChangeLayout;
